<template>
  <div class="sc-demo">
    <h1>Server & Client Demo</h1>

    <!-- Data fetched on the server before first render -->
    <section class="sc-demo__server">
      <h2>Server Side</h2>
      <p>This data was fetched during SSR — it's in the HTML before JS loads:</p>
      <div class="sc-demo__card" v-if="serverMessage">
        {{ serverMessage }}
      </div>
      <div class="sc-demo__card sc-demo__card--empty" v-else>
        No data returned from server.
      </div>
    </section>

    <!-- Interactions that only make sense in the browser -->
    <section class="sc-demo__client">
      <h2>Client Side</h2>
      <p>This runs after the page mounts in the browser:</p>
      <div class="sc-demo__card">
        <p>Window width: <strong>{{ windowWidth }}px</strong></p>
        <p>Time on page: <strong>{{ seconds }}s</strong></p>
        <p>Mouse position: <strong>{{ mouseX }}, {{ mouseY }}</strong></p>
      </div>
    </section>

    <a href="/s/gift-card-rewards/">← Back to Home</a>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'ServerClientDemo',

  data() {
    return {
      // Client-only state — not available during SSR
      windowWidth: 0,
      seconds: 0,
      mouseX: 0,
      mouseY: 0,
      timer: null,
    };
  },

  computed: {
    ...mapGetters(['serverMessage']),
  },

  /**
   * SERVER SIDE
   * Runs on the Node server before the HTML is rendered.
   * The store is populated here so the data is baked into
   * the HTML string that gets sent to the browser.
   * window, document, and this.$refs do NOT exist here.
   */
  serverPrefetch() {
    return this.$store.dispatch('fetchServerMessage');
  },

  /**
   * CLIENT SIDE
   * Runs after Vue has mounted the component in the browser DOM.
   * This is the first point where window, document, and refs exist.
   * SSR has already populated the store so serverMessage is available.
   */
  mounted() {
    // Browser APIs — not available on the server
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', this.onResize);
    window.addEventListener('mousemove', this.onMouseMove);

    // Start a timer — setTimeout/setInterval don't exist on the server
    this.timer = setInterval(() => {
      this.seconds++;
    }, 1000);

    // Client-side store fallback — in case SSR didn't run
    this.$store.dispatch('fetchServerMessage');
  },

  /**
   * CLIENT SIDE
   * Clean up listeners and timers when the component is removed.
   * Always clean up anything you set up in mounted().
   */
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('mousemove', this.onMouseMove);
    clearInterval(this.timer);
  },

  methods: {
    onResize() {
      this.windowWidth = window.innerWidth;
    },
    onMouseMove(e) {
      this.mouseX = e.clientX;
      this.mouseY = e.clientY;
    },
  },
};
</script>

<style lang="scss">
.sc-demo {
  padding: 40px;
  font-family: 'Macys Sans Regular', sans-serif;

  h1 {
    margin-bottom: 32px;
  }

  h2 {
    font-family: 'Macys Sans Bold', sans-serif;
    font-size: 18px;
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 12px;
    color: #555;
  }

  &__server,
  &__client {
    margin-bottom: 40px;
    padding: 24px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
  }

  &__server {
    border-top: 4px solid #2196F3;
  }

  &__client {
    border-top: 4px solid #E11F26;
  }

  &__card {
    background: #f5f5f5;
    padding: 16px;
    border-radius: 4px;
    font-size: 16px;
    line-height: 1.6;

    strong {
      font-family: 'Macys Sans Bold', sans-serif;
    }

    &--empty {
      color: #999;
      font-style: italic;
    }
  }
}
</style>
